'use client';
import { RefObject, useEffect, useRef, MutableRefObject } from 'react';
import { gsap } from 'gsap';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { ImageProps } from '@repo/ui/src/types/image';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import Image from '@repo/ui/src/components/Image';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useMounted } from '@repo/ui/src/hooks/useMounted';

interface GrillHeroProps {
  toutContent?: ToutContentProps;
  grillImage?: ImageProps;
}

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const gsapConfig = (
  ref: RefObject<HTMLElement>,
  tl: MutableRefObject<gsap.core.Timeline | undefined>,
) => {
  if (typeof window === 'undefined') return;

  const mm = gsap.matchMedia();

  if (tl.current) {
    tl.current.progress(0).kill();
  }

  mm.add(
    '(prefers-reduced-motion: no-preference)',
    (context) => {
      const ctx = context;

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          scrub: 0.5,
          start: 'top center',
          end: 'bottom 40%',
        },
      });

      tl.current = timeline;

      timeline
        .addLabel('start')
        .from('.grill-hero-content', {
          translateY: 50,
          opacity: 0,
          duration: 1,
        })
        .delay(5)
        .addLabel('smoke')
        .fromTo(
          '.smoke-wrapper',
          {
            yPercent: 100,
            opacity: 0,
          },
          {
            yPercent: 30,
            opacity: 0.8,
            duration: 3,
          },
          'smoke',
        )
        .from(
          '.smoke-image-1',
          {
            xPercent: -12,
            duration: 3,
          },
          'smoke',
        )
        .from(
          '.smoke-image-2',
          {
            xPercent: 6,
            duration: 3,
          },
          'smoke',
        )
        .to(
          '.grill-hero-content',
          {
            opacity: 0,
            duration: 1,
            delay: 1,
          },
          'smoke',
        );

      return () => {
        ctx.revert();
      };
    },
    ref,
  );
};

const GrillHero = ({ grillImage, toutContent }: GrillHeroProps) => {
  const ref = useRef<HTMLElement>(null);
  const tl = useRef<gsap.core.Timeline>();
  const isMounted = useMounted();
  const smokeImgUrl = buildImageSrc({ endpoint: 'traeger', name: 'transparent-smoke' });

  useEffect(() => {
    if (!isMounted) return;

    gsapConfig(ref, tl);

    return () => {
      if (tl.current) {
        tl.current.kill();
      }
      if (typeof window !== 'undefined') {
        ScrollTrigger.getAll().forEach((st) => st.kill());
      }
    };
  }, [isMounted]);

  if (!grillImage) return null;
  return (
    <section ref={ref}>
      <div className="grill-hero text-center bg-neutral-700 bg-speckle">
        <ToutContent
          dark
          divider
          flexible
          headingStyleVariant="h1"
          {...toutContent}
          className="grill-hero-content sticky top-0 left-0 right-0 px-6 py-16 lg:px-16"
        />

        <div className="overflow-hidden relative pointer-events-none">
          <div className="relative mx-auto w-full md:w-half z-10">
            <div className="hero-grill-image -mb-[20%] aspect-square relative">
              <Image
                layout="fill"
                src={buildImageSrc(grillImage?.image)}
                alt={grillImage?.image?.name}
              />
            </div>
          </div>

          <div className="smoke-wrapper absolute bottom-0 left-0 w-full pt-[32.25%] z-0 motion-reduce:opacity-80 motion-reduce:translate-y-1/3">
            {[1, 2].map((i: number) => (
              <div key={i} className={`smoke-image-${i} w-full h-full absolute bottom-0`}>
                <Image
                  src={smokeImgUrl}
                  className={classNames(i === 2 && '-scale-x-200')}
                  role="presentation"
                  alt=""
                  layout="fill"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrillHero;
